import {
  InputState,
  createDefaultInput,
  KEY_BINDINGS,
  GAMEPAD_BINDINGS,
  STICK_DEADZONE,
} from '@mine-or-die/shared';

/**
 * Input manager that captures keyboard, gamepad, and touch input
 * Provides a unified InputState each frame
 */
export class InputManager {
  private keysDown: Set<string> = new Set();
  private gamepadIndex: number | null = null;

  // Mouse aim (for keyboard+mouse control)
  private mouseX: number = 0;
  private mouseY: number = 0;
  private mouseWorldX: number = 0;
  private mouseWorldY: number = 0;
  private mouseButtonDown: boolean = false;

  // Ship position for calculating aim angle (set externally)
  private shipX: number = 0;
  private shipY: number = 0;

  // Touch controls
  private isTouchDevice: boolean = false;
  private touchActive: boolean = false;

  // Left joystick (movement)
  private leftTouchId: number | null = null;
  private leftTouchStartX: number = 0;
  private leftTouchStartY: number = 0;
  private leftTouchCurrentX: number = 0;
  private leftTouchCurrentY: number = 0;

  // Right joystick (aim/fire)
  private rightTouchId: number | null = null;
  private rightTouchStartX: number = 0;
  private rightTouchStartY: number = 0;
  private rightTouchCurrentX: number = 0;
  private rightTouchCurrentY: number = 0;
  private rightTouchActive: boolean = false;

  // Left joystick thrust state (for visual feedback)
  private leftTouchThrusting: boolean = false;

  constructor() {
    this.setupKeyboardListeners();
    this.setupGamepadListeners();
    this.setupMouseListeners();
    this.setupTouchListeners();
  }

  private setupKeyboardListeners(): void {
    window.addEventListener('keydown', (e) => {
      // Prevent default for game keys
      if (this.isGameKey(e.code)) {
        e.preventDefault();
      }
      this.keysDown.add(e.code);
    });

    window.addEventListener('keyup', (e) => {
      this.keysDown.delete(e.code);
    });

    // Clear keys when window loses focus
    window.addEventListener('blur', () => {
      this.keysDown.clear();
    });
  }

  private setupGamepadListeners(): void {
    window.addEventListener('gamepadconnected', (e) => {
      console.log(`Gamepad connected: ${e.gamepad.id}`);
      if (this.gamepadIndex === null) {
        this.gamepadIndex = e.gamepad.index;
      }
    });

    window.addEventListener('gamepaddisconnected', (e) => {
      console.log(`Gamepad disconnected: ${e.gamepad.id}`);
      if (this.gamepadIndex === e.gamepad.index) {
        this.gamepadIndex = null;
        // Try to find another gamepad
        const gamepads = navigator.getGamepads();
        for (let i = 0; i < gamepads.length; i++) {
          if (gamepads[i]) {
            this.gamepadIndex = i;
            break;
          }
        }
      }
    });
  }

  private setupMouseListeners(): void {
    window.addEventListener('mousemove', (e) => {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
    });

    window.addEventListener('mousedown', (e) => {
      if (e.button === 0) { // Left click
        this.mouseButtonDown = true;
      }
    });

    window.addEventListener('mouseup', (e) => {
      if (e.button === 0) { // Left click
        this.mouseButtonDown = false;
      }
    });

    // Clear mouse button when window loses focus
    window.addEventListener('blur', () => {
      this.mouseButtonDown = false;
    });

    // Prevent context menu on right-click in game
    window.addEventListener('contextmenu', (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'CANVAS') {
        e.preventDefault();
      }
    });
  }

  private setupTouchListeners(): void {
    // Detect touch device
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (!this.isTouchDevice) return;

    const handleTouchStart = (e: TouchEvent) => {
      // Only prevent default on game canvas, not UI elements
      const target = e.target as HTMLElement;
      if (target.tagName === 'CANVAS' || target.classList.contains('joystick-zone')) {
        e.preventDefault();
      }

      const screenMidX = window.innerWidth / 2;

      for (const touch of Array.from(e.changedTouches)) {
        if (touch.clientX < screenMidX && this.leftTouchId === null) {
          // Left side - movement
          this.leftTouchId = touch.identifier;
          this.leftTouchStartX = touch.clientX;
          this.leftTouchStartY = touch.clientY;
          this.leftTouchCurrentX = touch.clientX;
          this.leftTouchCurrentY = touch.clientY;
        } else if (touch.clientX >= screenMidX && this.rightTouchId === null) {
          // Right side - aim/fire
          this.rightTouchId = touch.identifier;
          this.rightTouchStartX = touch.clientX;
          this.rightTouchStartY = touch.clientY;
          this.rightTouchCurrentX = touch.clientX;
          this.rightTouchCurrentY = touch.clientY;
          this.rightTouchActive = true;
        }
      }
      this.touchActive = this.leftTouchId !== null || this.rightTouchId !== null;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'CANVAS' || target.classList.contains('joystick-zone')) {
        e.preventDefault();
      }

      for (const touch of Array.from(e.changedTouches)) {
        if (this.leftTouchId !== null && touch.identifier === this.leftTouchId) {
          this.leftTouchCurrentX = touch.clientX;
          this.leftTouchCurrentY = touch.clientY;
        }
        if (this.rightTouchId !== null && touch.identifier === this.rightTouchId) {
          this.rightTouchCurrentX = touch.clientX;
          this.rightTouchCurrentY = touch.clientY;
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      for (const touch of Array.from(e.changedTouches)) {
        if (this.leftTouchId !== null && touch.identifier === this.leftTouchId) {
          this.leftTouchId = null;
        }
        if (this.rightTouchId !== null && touch.identifier === this.rightTouchId) {
          this.rightTouchId = null;
          this.rightTouchActive = false;
        }
      }
      this.touchActive = this.leftTouchId !== null || this.rightTouchId !== null;
    };

    // Add listeners to window with passive: false to prevent scrolling
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: false });
  }

  private isGameKey(code: string): boolean {
    const allKeys: readonly string[] = [
      ...KEY_BINDINGS.thrustForward,
      ...KEY_BINDINGS.thrustBackward,
      ...KEY_BINDINGS.rotateLeft,
      ...KEY_BINDINGS.rotateRight,
      ...KEY_BINDINGS.shoot,
    ];
    return allKeys.includes(code);
  }

  private isKeyDown(keys: readonly string[]): boolean {
    return keys.some((key) => this.keysDown.has(key));
  }

  private applyDeadzone(value: number, deadzone: number): number {
    if (Math.abs(value) < deadzone) return 0;
    const sign = value > 0 ? 1 : -1;
    return sign * ((Math.abs(value) - deadzone) / (1 - deadzone));
  }

  /**
   * Set the player's ship position for aim angle calculation
   */
  setShipPosition(x: number, y: number): void {
    this.shipX = x;
    this.shipY = y;
  }

  /**
   * Set the mouse position in world coordinates
   */
  setMouseWorldPosition(x: number, y: number): void {
    this.mouseWorldX = x;
    this.mouseWorldY = y;
  }

  /**
   * Get the current input state
   */
  getInputState(): InputState {
    const input = createDefaultInput();

    // Try touch input first (for mobile devices)
    if (this.touchActive) {
      return this.getTouchInput(input);
    }

    // Try gamepad
    const gamepad = this.getGamepad();
    if (gamepad) {
      return this.getGamepadInput(gamepad, input);
    }

    // Fall back to keyboard
    return this.getKeyboardInput(input);
  }

  private getTouchInput(input: InputState): InputState {
    const maxDrag = 50; // Max pixels for full input
    const thrustThreshold = maxDrag * 0.8; // 80% of max = thrust zone
    const deadZone = 5; // Pixels of dead zone in center

    // Left stick - direction-based movement
    // Ship faces the direction the joystick points
    // Thrust activates only when knob is at edge (80-100% radius)
    if (this.leftTouchId !== null) {
      const dx = this.leftTouchCurrentX - this.leftTouchStartX;
      const dy = this.leftTouchCurrentY - this.leftTouchStartY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > deadZone) {
        // Calculate target angle from joystick direction
        // Note: In screen coordinates, Y is inverted (down is positive)
        // Adjust so that up on screen = up in game
        input.moveAngle = Math.atan2(dy, dx);

        // Thrust only when at edge (80-100% of max radius)
        if (distance > thrustThreshold) {
          input.thrust = 1.0;
          this.leftTouchThrusting = true;
        } else {
          input.thrust = 0;
          this.leftTouchThrusting = false;
        }
      } else {
        this.leftTouchThrusting = false;
      }

      // No manual rotation - ship turns toward moveAngle
      input.rotation = 0;
    } else {
      this.leftTouchThrusting = false;
    }

    // Right stick - aim angle + shooting
    if (this.rightTouchId !== null) {
      const dx = this.rightTouchCurrentX - this.rightTouchStartX;
      const dy = this.rightTouchCurrentY - this.rightTouchStartY;

      // Calculate aim angle from drag direction
      if (Math.abs(dx) > deadZone || Math.abs(dy) > deadZone) {
        input.aimAngle = Math.atan2(dy, dx);
      }

      // Shooting while right touch is active
      input.shooting = this.rightTouchActive;
    }

    return input;
  }

  private getGamepad(): Gamepad | null {
    if (this.gamepadIndex === null) return null;
    const gamepads = navigator.getGamepads();
    return gamepads[this.gamepadIndex] || null;
  }

  private getGamepadInput(gamepad: Gamepad, input: InputState): InputState {
    const axes = gamepad.axes;
    const buttons = gamepad.buttons;

    // Thrust (left stick Y, inverted)
    const thrustRaw = -axes[GAMEPAD_BINDINGS.thrustAxis] || 0;
    input.thrust = this.applyDeadzone(thrustRaw, STICK_DEADZONE);

    // Rotation (left stick X)
    const rotateRaw = axes[GAMEPAD_BINDINGS.rotateAxis] || 0;
    input.rotation = this.applyDeadzone(rotateRaw, STICK_DEADZONE);

    // Aim (right stick)
    const aimX = axes[GAMEPAD_BINDINGS.aimXAxis] || 0;
    const aimY = axes[GAMEPAD_BINDINGS.aimYAxis] || 0;

    if (Math.abs(aimX) > STICK_DEADZONE || Math.abs(aimY) > STICK_DEADZONE) {
      input.aimAngle = Math.atan2(aimY, aimX);
    }

    // Shoot (right trigger or bumper)
    const shootMain = buttons[GAMEPAD_BINDINGS.shootButton];
    const shootAlt = buttons[GAMEPAD_BINDINGS.shootButtonAlt];
    input.shooting =
      (shootMain && shootMain.pressed) || (shootAlt && shootAlt.pressed);

    return input;
  }

  private getKeyboardInput(input: InputState): InputState {
    // Thrust
    if (this.isKeyDown(KEY_BINDINGS.thrustForward)) {
      input.thrust = 1;
    } else if (this.isKeyDown(KEY_BINDINGS.thrustBackward)) {
      input.thrust = -0.5; // Reverse is slower
    }

    // Rotation from keyboard
    if (this.isKeyDown(KEY_BINDINGS.rotateLeft)) {
      input.rotation = -1;
    } else if (this.isKeyDown(KEY_BINDINGS.rotateRight)) {
      input.rotation = 1;
    }

    // Aim angle (mouse-based for keyboard)
    const dx = this.mouseWorldX - this.shipX;
    const dy = this.mouseWorldY - this.shipY;
    input.aimAngle = Math.atan2(dy, dx);

    // Shoot with keyboard OR left mouse button
    input.shooting = this.isKeyDown(KEY_BINDINGS.shoot) || this.mouseButtonDown;

    return input;
  }

  /**
   * Check if any input device is active
   */
  hasInput(): boolean {
    return this.keysDown.size > 0 || this.gamepadIndex !== null;
  }

  /**
   * Check if a gamepad is connected
   */
  hasGamepad(): boolean {
    return this.gamepadIndex !== null;
  }

  /**
   * Get raw mouse position
   */
  getMousePosition(): { x: number; y: number } {
    return { x: this.mouseX, y: this.mouseY };
  }

  /**
   * Check if this is a touch device
   */
  isTouchEnabled(): boolean {
    return this.isTouchDevice;
  }

  /**
   * Check if left joystick is in thrust zone
   */
  isLeftJoystickThrusting(): boolean {
    return this.leftTouchThrusting;
  }

  /**
   * Update joystick visual positions (call each frame)
   */
  updateJoystickVisuals(): void {
    if (!this.isTouchDevice) return;

    const leftKnob = document.getElementById('left-knob');
    const rightKnob = document.getElementById('right-knob');
    const leftZone = document.getElementById('left-joystick');
    const maxOffset = 45; // Max knob movement in pixels

    if (leftKnob && this.leftTouchId !== null) {
      const dx = Math.max(-maxOffset, Math.min(maxOffset, this.leftTouchCurrentX - this.leftTouchStartX));
      const dy = Math.max(-maxOffset, Math.min(maxOffset, this.leftTouchCurrentY - this.leftTouchStartY));
      leftKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    } else if (leftKnob) {
      leftKnob.style.transform = 'translate(-50%, -50%)';
    }

    // Update left joystick thrust visual
    if (leftZone) {
      if (this.leftTouchThrusting) {
        leftZone.classList.add('thrusting');
      } else {
        leftZone.classList.remove('thrusting');
      }
    }

    if (rightKnob && this.rightTouchId !== null) {
      const dx = Math.max(-maxOffset, Math.min(maxOffset, this.rightTouchCurrentX - this.rightTouchStartX));
      const dy = Math.max(-maxOffset, Math.min(maxOffset, this.rightTouchCurrentY - this.rightTouchStartY));
      rightKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    } else if (rightKnob) {
      rightKnob.style.transform = 'translate(-50%, -50%)';
    }
  }
}
